import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { LogOut, Users, Mail, Phone, MapPin, Calendar, AlertCircle, CheckCircle, X, MessageSquare, Plus, Loader2 } from 'lucide-react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filter, setFilter] = useState('all');
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }

    fetchSubmissions();
  }, [navigate]);

  useEffect(() => {
    if (selectedSubmission) {
      fetchComments(selectedSubmission.id);
    }
  }, [selectedSubmission]);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/all-submissions`);
      setSubmissions(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setLoading(false);
    }
  };

  const fetchComments = async (interestId) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/transportation-interest/${interestId}/comments`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
      setComments([]);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedSubmission) return;
    
    setIsAddingComment(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/transportation-interest/${selectedSubmission.id}/comment?comment_text=${encodeURIComponent(newComment)}`
      );
      
      setNewComment('');
      await fetchComments(selectedSubmission.id);
      await fetchSubmissions(); // Refresh list
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment');
    } finally {
      setIsAddingComment(false);
    }
  };

  const handleStatusChange = async (interestId, newStatus) => {
    setIsUpdatingStatus(true);
    try {
      await axios.put(`${BACKEND_URL}/api/transportation-interest/${interestId}/status?status=${newStatus}`);
      
      // Update local state
      setSubmissions(submissions.map(s => 
        s.id === interestId ? { ...s, status: newStatus } : s
      ));
      
      if (selectedSubmission && selectedSubmission.id === interestId) {
        setSelectedSubmission({ ...selectedSubmission, status: newStatus });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'contacted': return 'bg-yellow-100 text-yellow-800';
      case 'scheduled': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredSubmissions = filter === 'all' 
    ? submissions 
    : submissions.filter(s => s.status === filter);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0077BE] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading CRM data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_trusted-journeys-co/artifacts/jq989m8x_365%20Transportation%20Logo%20White.png"
                alt="No Limit 365 Transportation"
                className="h-10 w-auto"
                style={{ filter: 'invert(1)' }}
              />
              <div>
                <h1 className="text-xl font-bold text-[#0B0F14]">CRM Dashboard</h1>
                <p className="text-sm text-gray-500">Lead Management System</p>
              </div>
            </div>
            <Button onClick={handleLogout} variant="outline" className="flex items-center gap-2">
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-600 text-sm font-semibold">Total Leads</p>
              <Users className="w-5 h-5 text-[#0077BE]" />
            </div>
            <p className="text-3xl font-bold text-[#0B0F14]">{submissions.length}</p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-600 text-sm font-semibold">New</p>
              <AlertCircle className="w-5 h-5 text-blue-500" />
            </div>
            <p className="text-3xl font-bold text-[#0B0F14]">
              {submissions.filter(s => s.status === 'new').length}
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-600 text-sm font-semibold">Contacted</p>
              <Mail className="w-5 h-5 text-yellow-500" />
            </div>
            <p className="text-3xl font-bold text-[#0B0F14]">
              {submissions.filter(s => s.status === 'contacted').length}
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <p className="text-gray-600 text-sm font-semibold">Scheduled</p>
              <CheckCircle className="w-5 h-5 text-green-500" />
            </div>
            <p className="text-3xl font-bold text-[#0B0F14]">
              {submissions.filter(s => s.status === 'scheduled').length}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-200 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                filter === 'all' 
                  ? 'bg-[#0077BE] text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({submissions.length})
            </button>
            <button
              onClick={() => setFilter('new')}
              className={`px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                filter === 'new' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              New ({submissions.filter(s => s.status === 'new').length})
            </button>
            <button
              onClick={() => setFilter('contacted')}
              className={`px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                filter === 'contacted' 
                  ? 'bg-yellow-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Contacted ({submissions.filter(s => s.status === 'contacted').length})
            </button>
            <button
              onClick={() => setFilter('scheduled')}
              className={`px-4 py-2 rounded-xl font-semibold text-sm transition-colors ${
                filter === 'scheduled' 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Scheduled ({submissions.filter(s => s.status === 'scheduled').length})
            </button>
          </div>
        </div>

        {/* Submissions List */}
        <div className="space-y-4">
          {filteredSubmissions.length === 0 ? (
            <div className="bg-white p-12 rounded-2xl shadow-sm border border-gray-200 text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No leads found</p>
            </div>
          ) : (
            filteredSubmissions.map((submission) => (
              <div
                key={submission.id}
                onClick={() => setSelectedSubmission(submission)}
                className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-lg font-bold text-[#0B0F14]">
                        {submission.guardianName || submission.contactName || submission.contact_name}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(submission.status)}`}>
                        {submission.status}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800">
                        {submission.type === 'partnership' ? 'Partnership' : 'Transportation'}
                      </span>
                      {submission.comments && submission.comments.length > 0 && (
                        <span className="flex items-center gap-1 text-sm text-gray-500">
                          <MessageSquare className="w-4 h-4" />
                          {submission.comments.length}
                        </span>
                      )}
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="w-4 h-4" />
                        {submission.email}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Phone className="w-4 h-4" />
                        {submission.phone}
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="w-4 h-4" />
                        {submission.type === 'partnership' 
                          ? `Organization: ${submission.organizationName || submission.organization_name}`
                          : `Student: ${submission.studentName} (Age ${submission.studentAge})`
                        }
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="w-4 h-4" />
                        {new Date(submission.submitted_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  
                  <Button variant="outline" size="sm">
                    View Details
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Enhanced CRM Detail Modal */}
      {selectedSubmission && (
        <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl font-bold text-[#0B0F14]">
                  Lead Details - CRM View
                </DialogTitle>
                <button
                  onClick={() => setSelectedSubmission(null)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </DialogHeader>

            <div className="grid md:grid-cols-3 gap-6 mt-4">
              {/* Main Info - 2 columns */}
              <div className="md:col-span-2 space-y-6">
                {/* Status Management */}
                <div className="bg-gray-50 p-4 rounded-xl">
                  <Label className="text-sm font-semibold text-gray-700 mb-2 block">Lead Status</Label>
                  <Select 
                    value={selectedSubmission.status}
                    onValueChange={(value) => handleStatusChange(selectedSubmission.id, value)}
                    disabled={isUpdatingStatus}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Conditional Rendering Based on Type */}
                {selectedSubmission.type === 'transportation' ? (
                  <>
                    {/* Guardian Info */}
                    <div className="border-l-4 border-[#0077BE] pl-4">
                      <h3 className="font-bold text-lg mb-3">Guardian Information</h3>
                      <div className="space-y-2 text-sm">
                        <p><strong>Name:</strong> {selectedSubmission.guardianName}</p>
                        <p><strong>Email:</strong> <a href={`mailto:${selectedSubmission.email}`} className="text-[#0077BE] hover:underline">{selectedSubmission.email}</a></p>
                        <p><strong>Phone:</strong> <a href={`tel:${selectedSubmission.phone}`} className="text-[#0077BE] hover:underline">{selectedSubmission.phone}</a></p>
                      </div>
                    </div>

                    {/* Student Info */}
                    <div className="border-l-4 border-[#00B4D8] pl-4">
                      <h3 className="font-bold text-lg mb-3">Student Information</h3>
                      <div className="space-y-2 text-sm">
                        <p><strong>Name:</strong> {selectedSubmission.studentName}</p>
                        <p><strong>Age:</strong> {selectedSubmission.studentAge}</p>
                        <p><strong>Grade:</strong> {selectedSubmission.studentGrade}</p>
                      </div>
                    </div>

                    {/* Transportation Details */}
                    <div className="border-l-4 border-[#2ECC71] pl-4">
                      <h3 className="font-bold text-lg mb-3">Transportation Details</h3>
                      <div className="space-y-2 text-sm">
                        <p><strong>Pickup:</strong> {selectedSubmission.pickupLocation}</p>
                        <p><strong>Dropoff:</strong> {selectedSubmission.dropoffLocation}</p>
                        <p><strong>Schedule:</strong> {selectedSubmission.scheduleNeeds}</p>
                        <p><strong>Days:</strong> {selectedSubmission.transportationDays}</p>
                      </div>
                    </div>

                    {/* Special Requirements */}
                    {(selectedSubmission.specialRequirements || selectedSubmission.behavioralNeeds || selectedSubmission.medicalNeeds) && (
                      <div className="border-l-4 border-[#FFB380] pl-4">
                        <h3 className="font-bold text-lg mb-3">Special Requirements</h3>
                        <div className="space-y-2 text-sm">
                          {selectedSubmission.specialRequirements && (
                            <p><strong>Accommodations:</strong> {selectedSubmission.specialRequirements}</p>
                          )}
                          {selectedSubmission.behavioralNeeds && (
                            <p><strong>Behavioral Needs:</strong> {selectedSubmission.behavioralNeeds}</p>
                          )}
                          {selectedSubmission.medicalNeeds && (
                            <p><strong>Medical Needs:</strong> {selectedSubmission.medicalNeeds}</p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Assessment Questions */}
                    {(selectedSubmission.questionMissedSchool || selectedSubmission.questionOperationalStrain || 
                      selectedSubmission.questionCoordinatingTime || selectedSubmission.questionConsistency) && (
                      <div className="border-l-4 border-[#0077BE] pl-4">
                        <h3 className="font-bold text-lg mb-3">Assessment Responses</h3>
                        <div className="space-y-3 text-sm">
                          {selectedSubmission.questionMissedSchool && (
                            <div>
                              <p className="font-semibold text-gray-700">Students missing school?</p>
                              <p className="text-[#0077BE] font-semibold">{selectedSubmission.questionMissedSchool}</p>
                            </div>
                          )}
                          {selectedSubmission.questionOperationalStrain && (
                            <div>
                              <p className="font-semibold text-gray-700">Operational strain?</p>
                              <p className="text-[#0077BE] font-semibold">{selectedSubmission.questionOperationalStrain}</p>
                            </div>
                          )}
                          {selectedSubmission.questionCoordinatingTime && (
                            <div>
                              <p className="font-semibold text-gray-700">Time coordinating exceptions?</p>
                              <p className="text-[#0077BE] font-semibold">{selectedSubmission.questionCoordinatingTime}</p>
                            </div>
                          )}
                          {selectedSubmission.questionConsistency && (
                            <div>
                              <p className="font-semibold text-gray-700">Consistency challenges?</p>
                              <p className="text-[#0077BE] font-semibold">{selectedSubmission.questionConsistency}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Partnership Contact Info */}
                    <div className="border-l-4 border-[#0077BE] pl-4">
                      <h3 className="font-bold text-lg mb-3">Contact Information</h3>
                      <div className="space-y-2 text-sm">
                        <p><strong>Organization:</strong> {selectedSubmission.organizationName}</p>
                        <p><strong>Contact Name:</strong> {selectedSubmission.contactName}</p>
                        <p><strong>Email:</strong> <a href={`mailto:${selectedSubmission.email}`} className="text-[#0077BE] hover:underline">{selectedSubmission.email}</a></p>
                        <p><strong>Phone:</strong> <a href={`tel:${selectedSubmission.phone}`} className="text-[#0077BE] hover:underline">{selectedSubmission.phone}</a></p>
                      </div>
                    </div>

                    {/* Partnership Details */}
                    <div className="border-l-4 border-[#00B4D8] pl-4">
                      <h3 className="font-bold text-lg mb-3">Partnership Details</h3>
                      <div className="space-y-2 text-sm">
                        {selectedSubmission.district && (
                          <p><strong>District/Location:</strong> {selectedSubmission.district}</p>
                        )}
                        {selectedSubmission.studentCount && (
                          <p><strong>Student Count:</strong> {selectedSubmission.studentCount}</p>
                        )}
                      </div>
                    </div>

                    {/* Partnership Message */}
                    {selectedSubmission.message && (
                      <div className="border-l-4 border-[#2ECC71] pl-4">
                        <h3 className="font-bold text-lg mb-3">Message</h3>
                        <div className="space-y-2 text-sm">
                          <p className="text-gray-700">{selectedSubmission.message}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Comments/Notes Sidebar - 1 column */}
              <div className="md:col-span-1">
                <div className="bg-gray-50 p-4 rounded-xl sticky top-4">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-5 h-5 text-[#0077BE]" />
                    <h3 className="font-bold text-lg">Notes & Comments</h3>
                  </div>

                  {/* Comments List */}
                  <div className="space-y-3 mb-4 max-h-[400px] overflow-y-auto">
                    {comments.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No comments yet</p>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className="bg-white p-3 rounded-lg border border-gray-200">
                          <div className="flex items-start justify-between mb-1">
                            <p className="text-xs font-semibold text-gray-600">{comment.author}</p>
                            <p className="text-xs text-gray-400">
                              {new Date(comment.timestamp).toLocaleDateString()}
                            </p>
                          </div>
                          <p className="text-sm text-gray-700">{comment.text}</p>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Add Comment */}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Add Note</Label>
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Enter your note or comment..."
                      rows={3}
                      className="text-sm"
                    />
                    <Button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || isAddingComment}
                      className="w-full bg-[#0077BE] hover:bg-[#00B4D8]"
                      size="sm"
                    >
                      {isAddingComment ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <Plus className="w-4 h-4 mr-2" />
                          Add Note
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Metadata */}
                  <div className="mt-6 pt-4 border-t border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Lead ID</p>
                    <p className="text-xs font-mono text-gray-700 mb-3">{selectedSubmission.id}</p>
                    <p className="text-xs text-gray-500 mb-1">Submitted</p>
                    <p className="text-xs text-gray-700">{new Date(selectedSubmission.submitted_at).toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdminDashboard;
